<script lang="ts">
  import { onDestroy } from 'svelte';

  interface Properties {
    text?: string;
    label?: string;
    copiedLabel?: string;
    resetAfterMs?: number;
    /** Resolved at click time — use when the value can change between mount and click (e.g. live URL). */
    getText?: () => string;
  }

  const {
    text = '',
    label = 'Copy',
    copiedLabel = 'Copied!',
    resetAfterMs = 1500,
    getText,
  }: Properties = $props();

  const state = $state<{
    isCopied: boolean;
    timer: ReturnType<typeof setTimeout> | undefined;
  }>({ isCopied: false, timer: undefined });

  onDestroy(() => {
    clearTimeout(state.timer);
  });

  async function copy(): Promise<void> {
    const payload = getText ? getText() : text;
    try {
      await navigator.clipboard.writeText(payload);
    } catch {
      /* clipboard unavailable — button still visually confirms below */
    }
    state.isCopied = true;
    clearTimeout(state.timer);
    state.timer = setTimeout(() => {
      state.isCopied = false;
    }, resetAfterMs);
  }
</script>

<!-- Copies to the clipboard on click: `getText()` if provided (evaluated at
     click time), else `text`. Flashes `copiedLabel` for `resetAfterMs`
     (default 1500 ms), then restores `label`. If `navigator.clipboard` is
     unavailable, still flashes the confirmation. -->
<button type="button" class="clipboard-btn" onclick={copy}>
  {state.isCopied ? copiedLabel : label}
</button>

<style>
  .clipboard-btn {
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    color: var(--color-primary);
    padding: 4px 10px;
    border-radius: var(--radius-sm);
    cursor: pointer;
    font-size: var(--font-size-sm);
  }

  .clipboard-btn:hover {
    border-color: var(--color-primary);
    background: var(--color-surface-alt);
  }
</style>
